import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ComponentFactoryResolver, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild,
  ViewContainerRef
} from '@angular/core';
import { DynamicModalAbstractComponent } from '@ispm/layout/classes/dynamic-modal-abstract-component';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { GenomeViewerService } from '../../../services/genome-viewer.service';
import { AberrationsService } from '@ispm/aberrations/services/aberrations.service';
import { Subject } from 'rxjs/Subject';
import { AberrationNotesTabComponent } from '../aberration-notes-tab/aberration-notes-tab.component';
import { AberrationInfoTabComponent } from '../aberration-info-tab/aberration-info-tab.component';
import { IgvComponent } from '../igv/igv.component';
import { AberrationInfoService } from '../aberration-info-service/aberration-info.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { AberrationsActionCreatorService } from '@ispm/aberrations/services/aberrations-action-creator.service';
import { FormControl } from '@angular/forms';
import {
  AberrationsFilter,
  FilterModes,
  AberrationExtraDetailsConfig,
  Tabs,
  AberrationClassMap, maxNumberOfHeaders, headerScrollStep, Header
} from './aberration-extra-details-commons';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { of } from 'rxjs/observable/of';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import { FilterService } from '../../../../filter/services/filter.service';
import { get } from 'lodash';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { IAnnotationSearchableLink, IModalitySearch } from '@ispm/dashboard/aberrations/interfaces/annotation-searchable-links.interface';
import { MenuItem } from 'primeng/api/menuitem';
import { ResourceRestService } from '@ispm/workflow/services/resource-rest.service';
import { Resources } from '@ispm/workflow/constants/resources.enum';
import { TestService } from '../../services/test.service';
import { Itab } from "@ispm/shared/interfaces/Itab";

@Component({
  selector: 'aberration-extra-details',
  templateUrl: './aberration-extra-details.component.html',
  styleUrls: ['./aberration-extra-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AberrationExtraDetailsComponent extends DynamicModalAbstractComponent implements OnInit {

  @Input() currentAberration: any;
  @Input() revisionId: string;
  @Input() firstRevisionId: string;
  @Input() testTableData: any;
  @Input() currentModality: string;
  @Input() testTypeId: string;
  @Input() currentOpinion: any;
  @Input() filterSelected: boolean;
  @Input() igvOnly: boolean;
  @Input() currentSort: { dir: string, column: string, sortAsExpression: string };
  @Output() noteChanged: EventEmitter<any> = new EventEmitter();
  @Output() selectionChanged: EventEmitter<any> = new EventEmitter();

  selections: any;
  filteredAberrationsIds: string[];
  currentAberrationSubject = new ReplaySubject<any>(1);
  currentTab: string;
  currentTabSubject = new Subject<string>();
  selectionsSubject = new ReplaySubject<any>(1);
  headers: Header[];
  filterQueryString: string;
  currentFilterSubject = new ReplaySubject<AberrationsFilter>(1);
  currentFilter: AberrationsFilter;
  loading = true;
  tabs: Itab[];
  tabTypes = { };
  componentRef;
  aberrationsFilterControl: FormControl = new FormControl();
  enableFilterSelections = false;
  enableFilterQuery = false;
  enableHeaderScrollLeft;
  enableHeaderScrollRight;
  getTherapyMatches = false;
  getClinicalTrials = false;
  searchItems: Array<MenuItem> = [];

  @ViewChild('headersContent', {static: false}) private headersContent: ElementRef;

  @ViewChild('headersParent', {static: false}) private headersParent: ElementRef;

  @ViewChild('tabChild', { static: true, read: ViewContainerRef}) private target: ViewContainerRef;

  defaultConfig: AberrationExtraDetailsConfig = {
    allowSelections: true,
    tabs: []
  };

  onNext(payload: any): void {
  }

  onError(error): void {
  }

  onComplete(): void {
  }

  constructor(
    modalLoaderService: DynamicModalLoaderService,
    private genomeViewerService: GenomeViewerService,
    private aberrationInfoService: AberrationInfoService,
    private aberrationsService: AberrationsService,
    private therapiesService: TherapiesService,
    private translate: TranslateService,
    private testService:TestService,
    private aberrationsActionCreatorService: AberrationsActionCreatorService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private workflowConfigService: WorkflowConfigService,
    private filterService: FilterService,
    private cdr: ChangeDetectorRef,
  ) {
    super(modalLoaderService);
    this.tabTypes[Tabs.AberrationInfoTabName] = AberrationInfoTabComponent;
    this.tabTypes[Tabs.AberrationNotesTabName] = AberrationNotesTabComponent;
    this.tabTypes[Tabs.GenomeViewerTabName] = IgvComponent;
  }

  ngOnInit() {
    this.selections = this.currentOpinion ? Object.assign({ }, this.currentOpinion.selections) : undefined;
    let searchConfig = this.testService.getCurrentDynamicSearchableLinksConfig();

    if (searchConfig == null) {
      searchConfig = ['defaultLinks'];
    }

    // There is no take/takeUntil pipe calls here since the data is either taken from the store (take(1)) or an HTTP request
    this.aberrationsService.getSearchLinksConfiguration(searchConfig)
      .subscribe(config => {
        config.forEach(searchLinkConfig => {

          if (typeof searchLinkConfig.searchModalities[this.currentModality] !== 'undefined') {
            this.aberrationsService.generateModalitySearchLink(searchLinkConfig.searchModalities[this.currentModality], {
              aberration: this.currentAberration,
              cacheKey: this.workflowConfigService.getCacheKeySync()
            }).take(1).filter(link => !!link).subscribe(link => {
              this.searchItems.push(this.buildSearchItems(searchLinkConfig, link));
            });
          }
        });
      });

    this.aberrationsFilterControl.valueChanges
      .switchMap(filterMode => {
        let aberrationGroup;
        const translatedGroupName = this.translate.instant(filterMode, filterMode);

        switch (filterMode) {
          case FilterModes.Filtered:
            aberrationGroup = this.filteredAberrationsIds;
            break;
          case FilterModes.Selected:
            aberrationGroup = Object.keys(this.selections).filter(id => this.selections[id]);
            break;
        }

        if (aberrationGroup && this.currentAberration && aberrationGroup.indexOf(this.currentAberration.id) === -1) {
          return this.dynamicModalLoader.popupModal(ConfirmModalComponent, {
            title: 'Warning',
            content: `The aberration is out of the target \"${translatedGroupName}\" group`,
            approveBtn: 'OK',
            declineBtn: 'Cancel'
          }, ModalTypes.ALERT, 'ABERRATIONS_POPUP_MODAL_LOADER')
            .map(response => response.payload)
            .take(1)
            .switchMap(response => {
              switch (response) {
                case modalResponse.approve:

                  // Note: when current filter is 'Selected', it is possible that the first IDs in the 'selections'
                  //       array are related to a different modality. When invoking 'getAberrationById' with the
                  //       current modality BUT with the first aberration's ID which relates to other modality -->
                  //       the result will be a non-related aberration (see: GEN-65187).
                  //       Therefore, in case of 'Selected' filter, invoke 'nextAberration' which will ensure that the
                  //        correct aberration is retrieved.
                  if (filterMode === FilterModes.Selected) {
                    return new Observable<string>((observer) => {
                      this.nextAberration(filterMode);

                      observer.next(filterMode);
                      observer.complete();
                    });

                  }

                  return this.genomeViewerService.getAberrationById(
                      ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
                      this.testTypeId,
                      this.currentModality,
                      [aberrationGroup[0]]
                    )
                    .switchMap(aberrations => {
                      this.currentAberration = aberrations[0];
                      this.getExtraAberrationData();
                      return of(filterMode);
                    });
                case modalResponse.decline:
                default:
                  return of(this.currentFilter.mode);
              }
            });
        }
        return of(filterMode);
      })
      .subscribe(filterMode => this.updateFilter(filterMode));

    this.aberrationsActionCreatorService.getState(['filters'])
      .take(1)
      .switchMap((val: Map<string, any>) => {
        const aberrationClass = AberrationClassMap[(this.currentModality).toLowerCase()];
        const filterQueryObject = val.get(aberrationClass).filterQueryObject;
        this.filterQueryString = this.filterService.buildFilterString(
          filterQueryObject.selectedFilterValues ? filterQueryObject.selectedFilterValues : filterQueryObject
        );
        const filterMode = this.filterSelected ? FilterModes.Selected
          : this.filterQueryString ? FilterModes.Filtered : FilterModes.NotFiltered;
        this.enableFilterQuery = filterMode === FilterModes.Filtered;
        this.updateFilter(filterMode);

        return this.aberrationsService.getAberrationsIdsByFilter(
          ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
          this.testTypeId,
          this.currentModality,
          AberrationClassMap[this.currentModality],
          this.currentFilter.filterQueryString,
          this.currentSort
        );
      })
      .subscribe(data => this.filteredAberrationsIds = data.ids);

    if (this.igvOnly) {
      this.defaultConfig.allowSelections = this.defaultConfig.allowSelections && this.selections && this.currentAberration;
      this.defaultConfig.tabs.push({ name: Tabs.GenomeViewerTabName});
      this.testTableData.aberrationExtraDetails = this.defaultConfig;
    }

    this.tabs = this.testTableData.aberrationExtraDetails.tabs.map(tab => ({ name: tab.name}));
    this.currentTab = this.testTableData.aberrationExtraDetails.tabs[0].name;
    const infoTab = this.testTableData.aberrationExtraDetails.tabs.find(tab => tab.name === Tabs.AberrationInfoTabName);
    this.getTherapyMatches = !!(infoTab && infoTab.configuration && infoTab.configuration.therapyMatches);
    this.getClinicalTrials = !!(infoTab && infoTab.configuration && infoTab.configuration.clinicalTrials);
    this.getExtraAberrationData();
    this.currentTabSubject.subscribe(() => {
      this.createTabComponents();
    });
    this.currentTabSubject.next(this.currentTab);
    this.enableFilterSelections = this.hasSelections();
    this.selectionsSubject.next(this.selections);
  }

  buildSearchItems(searchLinkConfig: IAnnotationSearchableLink, link: string): MenuItem {
    const searchModality: IModalitySearch = searchLinkConfig.searchModalities[this.currentModality];
    let label;

    if (searchModality.label || searchModality.labelHtml) {
      label = searchModality.labelHtml ? searchModality.labelHtml : searchModality.label;
    } else {
      label = searchLinkConfig.id;
    }

    return {
      label: label,
      command: this.buildSearchCommand(link)
    };
  }

  buildSearchCommand(link: string): () => void {
    return () => {
      window.open(link);
    };
  }

  updateFilter(filterMode: FilterModes) {
    this.aberrationsFilterControl.setValue(filterMode, { emitEvent: false });

    if (!(this.currentFilter && filterMode === this.currentFilter.mode)) {
      this.currentFilter = {
        mode: filterMode as FilterModes,
        filterQueryString: filterMode === FilterModes.Filtered ? this.filterQueryString : ''
      };
      this.currentFilterSubject.next(this.currentFilter);
    }
  }

  createTabComponents() {
    if (this.componentRef) {
        this.componentRef.destroy();
    }

    const tab = this.testTableData.aberrationExtraDetails.tabs.find(currentTab => currentTab.name === this.currentTab);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.tabTypes[this.currentTab]);
    this.componentRef = this.target.createComponent(componentFactory);
    this.componentRef.location.nativeElement.styleClass = 'full-width';
    this.componentRef.instance['aberration'] = this.currentAberrationSubject;
    this.componentRef.instance['testTableData'] = this.testTableData;
    this.componentRef.instance['testTypeId'] = this.testTypeId;
    this.componentRef.instance['currentModality'] = this.currentModality;
    this.componentRef.instance['selections'] = this.selectionsSubject;
    this.componentRef.instance['firstRevisionId'] = this.firstRevisionId;
    this.componentRef.instance['revisionId'] = this.revisionId;
    this.componentRef.instance['configuration'] = tab.configuration;
    this.componentRef.instance['currentFilter'] = this.currentFilterSubject;
    this.componentRef.instance['noteChanged'].subscribe($event => this.noteUpdated($event));
    this.componentRef.instance['contentUpdated'].subscribe($event => this.setSpinner($event));
  }

  setSpinner($event) {
    this.loading = $event;
    this.cdr.markForCheck();
  }

  noteUpdated($event) {
    this.currentAberration.hasNotes = true;
    this.currentAberration.lastNoteText = $event.text;

    this.noteChanged.emit({
      currentAberration: this.currentAberration
    });
  }

  getExtraAberrationData() {
    this.loading = true;
    this.cdr.markForCheck();
    Observable.combineLatest(
      this.aberrationInfoService.getAberrationInfo(
        this.currentAberration,
        this.testTableData,
        ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
        this.currentModality
      ),
      this.getTherapyMatches ? this.therapiesService.getTherapies(true, this.currentAberration.id) : of([]),
      this.getClinicalTrials ? this.therapiesService.getTrialsByAberrationId(this.currentAberration.id) : of([]))
      .subscribe(([aberrationInfo, therapyMatches, clinicalTrials]) => {
        this.currentAberration.extra = aberrationInfo;
        this.currentAberration.therapyMatches = this.mapByProvider('provider', therapyMatches);
        this.currentAberration.clinicalTrials = this.mapByProvider('source', clinicalTrials);
        this.createHeaders(this.currentAberration);
        this.loading = false;
        this.currentAberrationSubject.next(this.currentAberration);
        this.cdr.markForCheck();
      });
  }

  private mapByProvider(providerProperty: string, entities: any[]) {
    const mappedEntities = { };
    entities.forEach(entity => {
      const provider = entity[providerProperty] ? entity[providerProperty] : 'nofone';
      entity[providerProperty] = provider;

      if (!mappedEntities[provider]) {
        mappedEntities[provider] = [];
      }

      mappedEntities[provider].push(entity);
    });
    return mappedEntities;
  }

  createHeaders(aberration): string[] {
    this.headers = this.testTableData.displayColumns[(this.currentModality).toLowerCase()]
      .filter(headerConfig => headerConfig.columnType)
      .slice(0, maxNumberOfHeaders)
      .map(headerConfig => {
          return {
            name: headerConfig.columnName,
            type: headerConfig.columnType,
            columnData: headerConfig,
            values: aberration
          };
      });

    setTimeout(() => {
      this.enableHeaderScrollRight = this.headersParent.nativeElement.clientWidth < this.headersContent.nativeElement.clientWidth;
    }, 0);

    return;
  }

  toggleSelection($event) {
    this.selections[this.currentAberration.id] = $event;
    this.enableFilterSelections = this.hasSelections();
    this.selectionsSubject.next(this.selections);
    this.selectionChanged.emit({
      currentAberration: this.currentAberration,
      selectionState: $event
    });
  }

  hasSelections() {
    if (this.selections) {
      return Object.keys(this.selections)
        .map(id => this.selections[id])
        .some(selected => selected);
    }

    return false;
  }

  onTabChange(tabName: string) {
    this.currentTab = tabName;
    this.currentTabSubject.next(this.currentTab);
  }

  scrollHeadersLeft() {
    const targetLocation = this.headersParent.nativeElement.scrollLeft - headerScrollStep < 0
      ? 0 : this.headersParent.nativeElement.scrollLeft - headerScrollStep;
    this.headersParent.nativeElement.scrollTo({ left: targetLocation, behavior: 'smooth'});
    this.updateScrollers(targetLocation);
  }

  scrollHeadersRight() {
    const targetLocation = this.headersParent.nativeElement.scrollLeft + headerScrollStep > this.headersContent.nativeElement.scrollWidth
      ? this.headersContent.nativeElement.scrollWidth : this.headersParent.nativeElement.scrollLeft + headerScrollStep;
    this.headersParent.nativeElement.scrollTo({ left: targetLocation, behavior: 'smooth'});
    this.updateScrollers(targetLocation);
  }

  updateScrollers(targetLocation: number) {
    const scrollWidth = this.headersParent.nativeElement.scrollWidth - this.headersParent.nativeElement.clientWidth;
    this.enableHeaderScrollLeft = targetLocation > 0;
    this.enableHeaderScrollRight = targetLocation < scrollWidth;
  }

  nextAberration(filterMode?: FilterModes) {
    const selectionsIds = this.getSelectionsIds(filterMode);
    this.genomeViewerService.getNextAberration(
        ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
        this.testTypeId,
        this.currentModality,
        AberrationClassMap[this.currentModality],
        this.currentFilter.filterQueryString,
        selectionsIds,
        this.currentAberration.id,
        this.currentSort
      )
      .subscribe(newAberration => {
        if (Array.isArray(newAberration) && (newAberration.length === 1)) {
          this.currentAberration = newAberration[0];
          this.getExtraAberrationData();
        }
      });
  }

  previousAberration() {
    const selectionsIds = this.getSelectionsIds();
    this.genomeViewerService.getPreviousAberration(
        ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
        this.testTypeId,
        this.currentModality,
        AberrationClassMap[this.currentModality],
        this.currentFilter.filterQueryString,
        selectionsIds,
        this.currentAberration.id,
        this.currentSort
      )
      .subscribe(newAberration => {
        if (Array.isArray(newAberration) && (newAberration.length === 1)) {
          this.currentAberration = newAberration[0];
          this.getExtraAberrationData();
        }
      });
  }

  private getSelectionsIds(filterMode?: FilterModes): string {

    if (! filterMode) {
      filterMode = this.currentFilter.mode;
    }

    if (this.selections && filterMode === FilterModes.Selected) {
      const selectedAberrationIds = Object.keys(this.selections).filter(aberrationId => this.selections[aberrationId]);

      return JSON.stringify(selectedAberrationIds);
    }

    return '';
  }

  shouldDisableSelectionForModality (): boolean {
    const defaultModalitiesWithoutAberrationSelection = ['wildtype'];
    const requiredAnnotationConfig = this.testTableData.requiredAnnotations.find((requiredAnnotation) => {
      return get(requiredAnnotation, 'type') == this.currentModality;
    });
    return get(requiredAnnotationConfig, 'variantSelectionOverride')
      ? get(requiredAnnotationConfig, 'variantSelectionOverride')
      : defaultModalitiesWithoutAberrationSelection.includes(this.currentModality)
    ;
  }

  ngOnDestroy() {
    this.currentAberrationSubject.next(true);
    this.currentTabSubject.next(null);
    this.selectionsSubject.next(true);
    this.currentFilterSubject.next(null);
    this.currentAberrationSubject.complete();
    this.currentTabSubject.complete();
    this.selectionsSubject.complete();
    this.currentFilterSubject.complete();
  }

  closeThisDialog(payload: any) {
    super.close(payload);
  }
}
